import axios, { type AxiosError } from 'axios'

/**
 * API manzili ikki bosqichda aniqlanadi (xsib-selfcare/xsib-admin naqshi,
 * bu yerda LOGIN/TOKEN qatlami yo'q — portal anonim, hech qanday sessiya
 * saqlanmaydi):
 *   1. `public/config.js` dagi `window.__XSIB_PORTAL_API_BASE__` —
 *      serverda tahrirlanadi, build qayta yig'ilmaydi
 *   2. `VITE_API_BASE_URL` — build vaqtidagi qiymat
 *   3. bo'sh — so'rovlar "/api/..." ga ketadi (dev'da Vite proxy,
 *      prod'da nginx `venue` moduli backend'iga uzatadi)
 */
declare global {
  interface Window {
    __XSIB_PORTAL_API_BASE__?: string
  }
}

const runtimeBase = typeof window !== 'undefined' ? window.__XSIB_PORTAL_API_BASE__ : undefined

export const api = axios.create({
  baseURL: (runtimeBase ?? import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/$/, ''),
  headers: { 'Content-Type': 'application/json' },
  // Mehmon ko'pincha yangi ulangan, sekin Wi-Fi'da — cheksiz kutish o'rniga
  // aniq muddat, aks holda "yuklanmoqda" spinneri abadiy osilib qoladi va
  // vazifaning "bo'sh ekran qoldirma" talabi buziladi (`translateError`
  // buni tarmoq xatosi sifatida ko'rsatadi va Qayta urinish tugmasi beradi).
  timeout: 15_000,
})

/**
 * Backend javobining yagona shakli (`uz.xsib.common.response.XsibResponse`).
 * Sahifalar buni ko'rmaydi — quyidagi interceptor ochib tashlaydi.
 */
export interface XsibEnvelope<T> {
  success: boolean
  requestId: string | null
  timeZone: string | null
  error: { code: number | null; tag: string | null; text: string | null } | null
  data: T
}

function isEnvelope(body: unknown): body is XsibEnvelope<unknown> {
  return typeof body === 'object' && body !== null && 'success' in body && 'data' in body
}

/** Envelope ichidagi xato — `translateError` uni `message` orqali o'qiydi. */
export class XsibApiError extends Error {
  constructor(
    message: string,
    readonly tag: string | null,
    readonly code: number | null,
    readonly requestId: string | null,
  ) {
    super(message)
    this.name = 'XsibApiError'
  }
}

/**
 * Envelope BITTA joyda ochiladi (xsib-admin bilan bir xil naqsh).
 *
 * ⚠️ Portalda sessiya yo'q, lekin javob shakli boshqa panellar bilan
 * BIR XIL — `venue` servisi ham envelope qaytaradi. Har chaqiruvda
 * `res.data.data` yozish o'rniga bitta joy: unutilgan chaqiruv faqat
 * mehmon ekranida bo'sh sahifa ko'rganda bilinardi.
 *
 * ⚠️ Zod sxemalari TEGILMAYDI — ular ichki (`data`) shaklni tekshiradi.
 */
api.interceptors.response.use((response) => {
  const body: unknown = response.data
  if (isEnvelope(body)) {
    if (!body.success) {
      return Promise.reject(
        new XsibApiError(
          // ⚠️ Qotirilgan o'zbekcha matn TURMASIN — u `translatePortalError` orqali
          // to'g'ridan-to'g'ri ekranga chiqadi. Bo'sh satr `error.generic` ga tushadi.
          body.error?.text ?? '',
          body.error?.tag ?? null,
          body.error?.code ?? null,
          body.requestId,
        ),
      )
    }
    response.data = body.data
  }
  return response
})

/**
 * ⚠️ Bu yerda xsib-selfcare'dagi kabi 401 → logout interceptori YO'Q va
 * bo'lmaydi ham: portalda token/sessiya umuman yo'q, GuestPortalController
 * endpointlari @RequiresPermission'siz ochiq (`docs/00-ARCHITECTURE.md`
 * §8) — himoya boshqa qatlamda (portal uchun alohida kalit/rate limit,
 * `app` xavfsizlik zanjirida). Xatolarni har chaqiruvchi o'zi ko'radi
 * (`shared/api/errors.ts`).
 */
export function isAxiosErrorWithStatus(error: unknown): error is AxiosError {
  return axios.isAxiosError(error)
}
